import React, { useState, useEffect } from 'react';
import { Leaf, Plus, Target, CreditCard, FileText, Sparkles, Loader2, TrendingDown, TrendingUp } from 'lucide-react';
import { carbonAPI, exportAPI } from '../../services/api';
import AssetSelect from '../../components/AssetSelect';
import ExportMenu from '../../components/ExportMenu';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const TABS = [
  { id: 'ledger',   label: 'Emissions Ledger', icon: Leaf      },
  { id: 'targets',  label: 'Targets',          icon: Target    },
  { id: 'credits',  label: 'Carbon Credits',   icon: CreditCard},
  { id: 'csrd',     label: 'CSRD Report',      icon: FileText  },
];

const SCOPE_COLOR = {
  scope_1: 'bg-red-100 text-red-700',
  scope_2: 'bg-amber-100 text-amber-700',
  scope_3: 'bg-blue-100 text-blue-700',
};

const CREDIT_COLOR = {
  earned:    'bg-emerald-100 text-emerald-700',
  purchased: 'bg-blue-100 text-blue-700',
  sold:      'bg-purple-100 text-purple-700',
  retired:   'bg-gray-100 text-gray-600',
};

function Modal({ title, onClose, children, onSubmit, loading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>
        <div className="px-6 py-5 space-y-4">{children}</div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={onSubmit} disabled={loading}
            className="flex items-center gap-2 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60">
            {loading && <Loader2 size={14} className="animate-spin"/>} Save
          </button>
        </div>
      </div>
    </div>
  );
}

const F = ({ label, children }) => (
  <div><label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>{children}</div>
);
const I = (p) => <input {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30"/>;
const S = ({ children, ...p }) => <select {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30 bg-white">{children}</select>;

const currentYear = new Date().getFullYear();
const YEARS = Array.from({length:5},(_,i)=>currentYear-i);
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function CarbonPage() {
  const { hasRole } = useAuth();
  const canWrite = hasRole('super_admin','compliance');

  const [tab, setTab]               = useState('ledger');
  const [emissions, setEmissions]   = useState([]);
  const [targets, setTargets]       = useState([]);
  const [credits, setCredits]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [yearFilter, setYear]       = useState(String(currentYear));
  const [csrdReport, setCsrdReport] = useState('');
  const [csrdYear, setCsrdYear]     = useState(String(currentYear));
  const [csrdLoading, setCsrdLoading] = useState(false);

  // Modals
  const [emModal, setEmModal]       = useState(false);
  const [tgModal, setTgModal]       = useState(false);
  const [crModal, setCrModal]       = useState(false);
  const [saving, setSaving]         = useState(false);

  const [emForm, setEmForm] = useState({ asset_id:'', period_year: currentYear, period_month: 1, scope:'scope_1', category:'', amount_tco2e:'', data_quality:'calculated', source_notes:'' });
  const [tgForm, setTgForm] = useState({ target_year: currentYear, scope:'total', target_tco2e:'', baseline_year:'', baseline_tco2e:'', notes:'' });
  const [crForm, setCrForm] = useState({ credit_type:'earned', amount_tco2e:'', price_per_tonne:'', total_value:'', registry:'', vintage_year:'', transaction_date: new Date().toISOString().split('T')[0], counterparty:'', notes:'' });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [e, t, c] = await Promise.all([
        carbonAPI.emissions({ year: yearFilter }),
        carbonAPI.targets(),
        carbonAPI.credits(),
      ]);
      setEmissions(e.data.data.items || e.data.data);
      setTargets(t.data.data);
      setCredits(c.data.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, [yearFilter]);

  const saveEmission = async () => {
    setSaving(true);
    try { await carbonAPI.addEmission(emForm); toast.success('Emission record added'); setEmModal(false); loadAll(); }
    catch {} finally { setSaving(false); }
  };

  const saveTarget = async () => {
    setSaving(true);
    try { await carbonAPI.setTarget(tgForm); toast.success('Target saved'); setTgModal(false); loadAll(); }
    catch {} finally { setSaving(false); }
  };

  const saveCredit = async () => {
    setSaving(true);
    try { await carbonAPI.addCredit(crForm); toast.success('Carbon credit logged'); setCrModal(false); loadAll(); }
    catch {} finally { setSaving(false); }
  };

  const generateCSRD = async () => {
    setCsrdLoading(true);
    try {
      const { data } = await carbonAPI.generateCSRD(parseInt(csrdYear));
      setCsrdReport(data.data.narrative);
      toast.success('CSRD report generated');
    } catch {} finally { setCsrdLoading(false); }
  };

  // Aggregates for ledger
  const byScope = emissions.reduce((acc,e) => {
    acc[e.scope] = (acc[e.scope]||0) + parseFloat(e.amount_tco2e||0);
    return acc;
  }, {});
  const totalTco2e = Object.values(byScope).reduce((a,b)=>a+b,0);

  // Find annual target
  const annualTarget = targets.find(t=>t.target_year==yearFilter&&t.scope==='total');
  const progressPct  = annualTarget ? Math.min(100,(totalTco2e/annualTarget.target_tco2e)*100) : null;

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Carbon Accounting</h1>
          <p className="text-sm text-gray-500">Scope 1, 2 & 3 emissions · EU Taxonomy · CSRD</p>
        </div>
        <ExportMenu exports={[
          { label:'Excel (Emissions)', apiFn:()=>exportAPI.carbonExcel(yearFilter), filename:`carbon-${yearFilter}.xlsx` },
          { label:'CSRD PDF',         apiFn:()=>exportAPI.csrdPDF(yearFilter),    filename:`csrd-${yearFilter}.pdf` },
        ]}/>
        <select value={yearFilter} onChange={e=>setYear(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/30">
          {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[['scope_1','Scope 1 (Direct)','bg-red-50','text-red-600'],
          ['scope_2','Scope 2 (Indirect)','bg-amber-50','text-amber-600'],
          ['scope_3','Scope 3 (Value Chain)','bg-blue-50','text-blue-600']].map(([scope,label,bg,tc])=>(
          <div key={scope} className={clsx('rounded-xl border border-gray-200 p-5',bg)}>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
            <p className={clsx('text-2xl font-bold mt-1',tc)}>{(byScope[scope]||0).toLocaleString(undefined,{maximumFractionDigits:1})} <span className="text-sm font-normal">tCO₂e</span></p>
          </div>
        ))}
      </div>

      {/* Target progress */}
      {annualTarget && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-700">Annual Carbon Target {yearFilter}</p>
            <p className="text-sm font-bold text-gray-900">{totalTco2e.toLocaleString(undefined,{maximumFractionDigits:0})} / {Number(annualTarget.target_tco2e).toLocaleString()} tCO₂e</p>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <div className={clsx('h-full rounded-full transition-all',progressPct>=90?'bg-danger':progressPct>=70?'bg-warn':'bg-accent')}
              style={{width:`${progressPct}%`}}/>
          </div>
          <p className="text-xs text-gray-400 mt-1">{progressPct?.toFixed(1)}% of annual target used</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(t=>{ const Icon=t.icon; return (
          <button key={t.id} onClick={()=>setTab(t.id)}
            className={clsx('flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab===t.id?'border-primary-500 text-primary-600':'border-transparent text-gray-500 hover:text-gray-700')}>
            <Icon size={14}/>{t.label}
          </button>
        );})}
      </div>

      {/* Emissions Ledger */}
      {tab==='ledger' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Emissions Records — {yearFilter}</h3>
            {canWrite && <button onClick={()=>setEmModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600"><Plus size={12}/>Add Record</button>}
          </div>
          {loading ? <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-gray-400"/></div>
          : emissions.length===0 ? <div className="text-center py-12 text-gray-400 text-sm">No emissions recorded for {yearFilter}.</div>
          : <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>{['Period','Asset','Scope','Category','Amount tCO₂e','Quality'].map(h=><th key={h} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {emissions.map((e,i)=>(
                  <tr key={e.id} className={i%2===0?'bg-white':'bg-surface'}>
                    <td className="px-4 py-3 text-gray-600">{MONTHS[e.period_month-1]} {e.period_year}</td>
                    <td className="px-4 py-3 text-gray-600">{e.asset_name||'Portfolio'}</td>
                    <td className="px-4 py-3"><span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium',SCOPE_COLOR[e.scope])}>{e.scope?.replace('_',' ').toUpperCase()}</span></td>
                    <td className="px-4 py-3 text-gray-600">{e.category||'—'}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{Number(e.amount_tco2e).toLocaleString(undefined,{maximumFractionDigits:2})}</td>
                    <td className="px-4 py-3 capitalize text-gray-500">{e.data_quality}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>}
        </div>
      )}

      {/* Targets */}
      {tab==='targets' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            {canWrite && <button onClick={()=>setTgModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600"><Plus size={14}/>Set Target</button>}
          </div>
          {targets.length===0 ? <div className="bg-white rounded-xl border border-gray-200 text-center py-12 text-gray-400 text-sm">No targets set yet.</div>
          : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {targets.map(t=>(
                <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500 uppercase">{t.scope.replace('_',' ')}</span>
                    <span className="text-sm font-bold text-gray-500">{t.target_year}</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{Number(t.target_tco2e).toLocaleString()} <span className="text-sm font-normal text-gray-500">tCO₂e</span></p>
                  {t.baseline_tco2e && <p className="text-xs text-gray-400 mt-1">Baseline ({t.baseline_year}): {Number(t.baseline_tco2e).toLocaleString()} tCO₂e</p>}
                </div>
              ))}
            </div>}
        </div>
      )}

      {/* Carbon Credits */}
      {tab==='credits' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Carbon Credit Ledger</h3>
            {canWrite && <button onClick={()=>setCrModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600"><Plus size={12}/>Log Credit</button>}
          </div>
          {credits.length===0 ? <div className="text-center py-12 text-gray-400 text-sm">No carbon credits logged yet.</div>
          : <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>{['Date','Type','Amount tCO₂e','Price/t','Value','Registry','Counterparty'].map(h=><th key={h} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {credits.map((c,i)=>(
                  <tr key={c.id} className={i%2===0?'bg-white':'bg-surface'}>
                    <td className="px-4 py-3 text-gray-600">{new Date(c.transaction_date).toLocaleDateString('en-GB')}</td>
                    <td className="px-4 py-3"><span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize',CREDIT_COLOR[c.credit_type])}>{c.credit_type}</span></td>
                    <td className="px-4 py-3 font-medium">{Number(c.amount_tco2e).toLocaleString()}</td>
                    <td className="px-4 py-3 text-gray-600">{c.price_per_tonne?`€${c.price_per_tonne}`:'—'}</td>
                    <td className="px-4 py-3 text-gray-600">{c.total_value?`€${Number(c.total_value).toLocaleString()}`:'—'}</td>
                    <td className="px-4 py-3 text-gray-600">{c.registry||'—'}</td>
                    <td className="px-4 py-3 text-gray-600">{c.counterparty||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>}
        </div>
      )}

      {/* CSRD Report */}
      {tab==='csrd' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div><h3 className="text-sm font-semibold text-gray-700">CSRD Report Generator</h3>
              <p className="text-xs text-gray-400 mt-0.5">AI writes the narrative sections based on your emissions and taxonomy data</p>
            </div>
            <div className="flex items-center gap-2">
              <select value={csrdYear} onChange={e=>setCsrdYear(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white">
                {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
              </select>
              <button onClick={generateCSRD} disabled={csrdLoading||!canWrite}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-60">
                {csrdLoading?<Loader2 size={14} className="animate-spin"/>:<Sparkles size={14}/>}
                {csrdLoading?'Generating…':'Generate'}
              </button>
            </div>
          </div>
          {csrdLoading ? (
            <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
              <Loader2 size={20} className="animate-spin text-primary-500"/>
              <p className="text-sm">AI is writing your CSRD narrative…</p>
            </div>
          ) : csrdReport ? (
            <div className="bg-primary-50 border border-primary-100 rounded-xl p-5 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{csrdReport}</div>
          ) : (
            <div className="text-center py-16 text-gray-400">
              <FileText size={32} className="mx-auto mb-3 text-gray-300"/>
              <p className="text-sm">Select a year and click Generate to produce an AI-written CSRD narrative based on your emissions, targets, and EU Taxonomy data.</p>
            </div>
          )}
        </div>
      )}

      {/* Add Emission Modal */}
      {emModal && <Modal title="Log Emission Record" onClose={()=>setEmModal(false)} onSubmit={saveEmission} loading={saving}>
        <div className="grid grid-cols-2 gap-4">
          <F label="Year"><I type="number" value={emForm.period_year} onChange={e=>setEmForm(f=>({...f,period_year:e.target.value}))}/></F>
          <F label="Month"><S value={emForm.period_month} onChange={e=>setEmForm(f=>({...f,period_month:e.target.value}))}>
            {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
          </S></F>
          <F label="Asset (optional)"><AssetSelect value={emForm.asset_id||''} onChange={e=>setEmForm(f=>({...f,asset_id:e.target.value}))} placeholder="Portfolio-level (no specific asset)"/></F>
          <F label="Scope"><S value={emForm.scope} onChange={e=>setEmForm(f=>({...f,scope:e.target.value}))}>
            <option value="scope_1">Scope 1 — Direct</option>
            <option value="scope_2">Scope 2 — Indirect</option>
            <option value="scope_3">Scope 3 — Value Chain</option>
          </S></F>
          <F label="Amount (tCO₂e)"><I type="number" step="0.0001" value={emForm.amount_tco2e} onChange={e=>setEmForm(f=>({...f,amount_tco2e:e.target.value}))}/></F>
          <F label="Category"><I value={emForm.category} onChange={e=>setEmForm(f=>({...f,category:e.target.value}))} placeholder="e.g. Fuel combustion"/></F>
          <F label="Data Quality"><S value={emForm.data_quality} onChange={e=>setEmForm(f=>({...f,data_quality:e.target.value}))}>
            <option value="measured">Measured</option>
            <option value="calculated">Calculated</option>
            <option value="estimated">Estimated</option>
          </S></F>
        </div>
        <F label="Source Notes"><textarea value={emForm.source_notes} onChange={e=>setEmForm(f=>({...f,source_notes:e.target.value}))} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500/30"/></F>
      </Modal>}

      {/* Set Target Modal */}
      {tgModal && <Modal title="Set Carbon Target" onClose={()=>setTgModal(false)} onSubmit={saveTarget} loading={saving}>
        <div className="grid grid-cols-2 gap-4">
          <F label="Year"><I type="number" value={tgForm.target_year} onChange={e=>setTgForm(f=>({...f,target_year:e.target.value}))}/></F>
          <F label="Scope"><S value={tgForm.scope} onChange={e=>setTgForm(f=>({...f,scope:e.target.value}))}>
            <option value="total">Total</option>
            <option value="scope_1">Scope 1</option>
            <option value="scope_2">Scope 2</option>
            <option value="scope_3">Scope 3</option>
          </S></F>
          <F label="Target (tCO₂e)"><I type="number" value={tgForm.target_tco2e} onChange={e=>setTgForm(f=>({...f,target_tco2e:e.target.value}))}/></F>
          <F label="Baseline Year"><I type="number" value={tgForm.baseline_year} onChange={e=>setTgForm(f=>({...f,baseline_year:e.target.value}))}/></F>
          <F label="Baseline (tCO₂e)"><I type="number" value={tgForm.baseline_tco2e} onChange={e=>setTgForm(f=>({...f,baseline_tco2e:e.target.value}))}/></F>
        </div>
      </Modal>}

      {/* Log Credit Modal */}
      {crModal && <Modal title="Log Carbon Credit" onClose={()=>setCrModal(false)} onSubmit={saveCredit} loading={saving}>
        <div className="grid grid-cols-2 gap-4">
          <F label="Type"><S value={crForm.credit_type} onChange={e=>setCrForm(f=>({...f,credit_type:e.target.value}))}>
            <option value="earned">Earned</option>
            <option value="purchased">Purchased</option>
            <option value="sold">Sold</option>
            <option value="retired">Retired</option>
          </S></F>
          <F label="Amount (tCO₂e)"><I type="number" step="0.0001" value={crForm.amount_tco2e} onChange={e=>setCrForm(f=>({...f,amount_tco2e:e.target.value}))}/></F>
          <F label="Price per Tonne (€)"><I type="number" step="0.01" value={crForm.price_per_tonne} onChange={e=>setCrForm(f=>({...f,price_per_tonne:e.target.value}))}/></F>
          <F label="Total Value (€)"><I type="number" step="0.01" value={crForm.total_value} onChange={e=>setCrForm(f=>({...f,total_value:e.target.value}))}/></F>
          <F label="Transaction Date"><I type="date" value={crForm.transaction_date} onChange={e=>setCrForm(f=>({...f,transaction_date:e.target.value}))}/></F>
          <F label="Vintage Year"><I type="number" value={crForm.vintage_year} onChange={e=>setCrForm(f=>({...f,vintage_year:e.target.value}))}/></F>
          <F label="Registry"><I value={crForm.registry} onChange={e=>setCrForm(f=>({...f,registry:e.target.value}))} placeholder="e.g. Verra, Gold Standard"/></F>
          <F label="Counterparty"><I value={crForm.counterparty} onChange={e=>setCrForm(f=>({...f,counterparty:e.target.value}))}/></F>
        </div>
      </Modal>}
    </div>
  );
}
